import React from 'react';
import { Button, Space, Input } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { type UseSearchReturn } from '@/hooks/useSearch';

export interface SearchBarProps {
  /**
   * 搜索Hook返回值
   */
  search: UseSearchReturn;
  /**
   * 搜索框占位符
   */
  placeholder?: string;
  /**
   * 搜索框宽度
   */
  width?: number | string;
  /**
   * 是否显示重置按钮
   */
  showReset?: boolean;
  /**
   * 是否显示搜索按钮
   */
  showSearchButton?: boolean;
  /**
   * 是否禁用搜索框
   */
  disabled?: boolean;
  /**
   * 搜索按钮加载状态
   */
  loading?: boolean;
  /**
   * 搜索按钮文本
   */
  searchButtonText?: string;
  /**
   * 重置按钮文本
   */
  resetButtonText?: string;
  /**
   * 额外的操作按钮
   */
  extraActions?: React.ReactNode;
  /**
   * 样式类名
   */
  className?: string;
  /**
   * 样式
   */
  style?: React.CSSProperties;
}

/**
 * 搜索栏组件
 * 提供统一的搜索界面和交互逻辑
 */
export const SearchBar: React.FC<SearchBarProps> = ({
  search,
  placeholder = '请输入搜索关键词',
  width = 300,
  showReset = true,
  showSearchButton = true,
  disabled = false,
  loading = false,
  searchButtonText = '搜索',
  resetButtonText = '重置',
  extraActions,
  className,
  style,
}) => {
  const {
    searchValue,
    setSearchValue,
    isSearching,
    handleSearch,
    handleResetSearch,
    handleSearchKeyPress,
  } = search;

  return (
    <Space wrap className={className} style={style}>
      {/* 搜索区域 */}
      <Space.Compact>
        <Input
          placeholder={placeholder}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          style={{ width }}
          allowClear
          disabled={disabled}
        />
        {showSearchButton && (
          <Button
            type='primary'
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
          >
            {searchButtonText}
          </Button>
        )}
      </Space.Compact>

      {/* 重置按钮 */}
      {showReset && isSearching && (
        <Button icon={<ReloadOutlined />} onClick={handleResetSearch}>
          {resetButtonText}
        </Button>
      )}

      {/* 额外的操作按钮 */}
      {extraActions}
    </Space>
  );
};

export default SearchBar;
